import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { useSkinSession } from '../context/SkinSessionContext';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import {
  loadRememberedLogin,
  onRememberMeToggle,
  persistRememberedLogin,
} from '../../shared/loginRememberMe';
import {
  SALON_HUB_PATH,
  SALON_LOGIN_DEVICE_ID_KEY,
  SALON_LOGIN_FLAG_KEY,
  SALON_LOGIN_SESSION_TOKEN_KEY,
  SALON_LOGIN_USERNAME_KEY,
  SALON_ORGANIZATION_ID_KEY,
  SALON_ORG_PLAN_KEY,
} from '../config/salonApp';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { normalizeOrgPlan } from '../../beauty-shared/config/planFeatures';
import {
  clearSalonAuthStorage,
  hasActiveSalonLogin,
  ensureSalonLoginValidated,
  markSalonLoginServerValidated,
  shouldClearSalonSessionOnValidateFailure,
  applyServerSessionExpiry,
  setSalonLoginTimeoutMinutes,
} from '../utils/salonAuth';
import './LoginPage.css';

function getOrCreateSalonDeviceId() {
  try {
    const existing = sessionStorage.getItem(SALON_LOGIN_DEVICE_ID_KEY);
    if (existing && String(existing).trim()) return String(existing).trim();
    const generated =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `salon-device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SALON_LOGIN_DEVICE_ID_KEY, generated);
    return generated;
  } catch (_) {
    return `salon-device-fallback-${Date.now()}`;
  }
}

const REMEMBER_PREFIX = 'salon_app';

const LoginPage = () => {
  const navigate = useNavigate();
  const { setToken } = useSkinSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const { rememberMe: savedRemember, identifier } = loadRememberedLogin(REMEMBER_PREFIX);
    setRememberMe(savedRemember);
    if (savedRemember && identifier) {
      setUsername(identifier);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkExistingSession() {
      if (!hasActiveSalonLogin()) return;

      const result = await ensureSalonLoginValidated();
      if (cancelled) return;

      if (result.ok) {
        navigate(SALON_HUB_PATH, { replace: true });
        return;
      }

      if (shouldClearSalonSessionOnValidateFailure(result)) {
        clearSalonAuthStorage();
        if (result.code === 'ACCOUNT_INACTIVE') {
          setError(result.error || 'This salon account is inactive. Please contact your administrator.');
        }
      }
    }

    checkExistingSession();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const user = username.trim();
    if (!user || !password) {
      setError('Please enter username and password.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const base = getApiBaseUrl();
      const deviceId = getOrCreateSalonDeviceId();
      const response = await fetchWithTimeout(`${base}${SALON_API_PREFIX}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Salon-Device-Id': deviceId,
        },
        body: JSON.stringify({ username: user, password, device_id: deviceId }),
        timeoutMs: 20000,
        skipSalonAuthHeaders: true,
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.success) {
        const baseError = result?.error || 'Invalid username or password.';
        if (result?.code === 'ALREADY_LOGGED_IN_OTHER_DEVICE' && result?.can_create_new_login) {
          setError(`${baseError} Your ${result?.plan || 'current'} plan still has capacity. Create a new login for the additional device.`);
          return;
        }
        if (result?.code === 'ORG_DEVICE_LIMIT_REACHED') {
          const limit = result?.device_limit;
          const active = result?.active_logins;
          setError(`${baseError}${Number.isFinite(limit) ? ` Active: ${active}/${limit}.` : ''}`);
          return;
        }
        setError(baseError);
        return;
      }

      const appToken = String(result?.app_session_token || '').trim();
      const loginSessionToken = String(result?.login_session_token || '').trim();
      const sessionExpiresAt = String(result?.session_expires_at || '').trim();
      if (!appToken) {
        setError('No app token mapped for this login.');
        return;
      }
      if (!loginSessionToken || !sessionExpiresAt) {
        setError('Login session metadata is missing. Please contact support.');
        return;
      }

      try {
        sessionStorage.setItem(SALON_LOGIN_FLAG_KEY, '1');
        sessionStorage.setItem(SALON_LOGIN_SESSION_TOKEN_KEY, loginSessionToken);
        sessionStorage.setItem(SALON_LOGIN_USERNAME_KEY, user);
        if (result?.timeout_minutes) {
          setSalonLoginTimeoutMinutes(result.timeout_minutes);
        }
        applyServerSessionExpiry(sessionExpiresAt);
        if (result?.organization_id) {
          sessionStorage.setItem(SALON_ORGANIZATION_ID_KEY, String(result.organization_id));
        }
        if (result?.plan) {
          sessionStorage.setItem(SALON_ORG_PLAN_KEY, normalizeOrgPlan(result.plan));
        }
        markSalonLoginServerValidated();
      } catch (_) {}

      setToken(appToken);
      persistRememberedLogin(REMEMBER_PREFIX, rememberMe, user);

      navigate(SALON_HUB_PATH, { replace: true });
      window.setTimeout(() => {
        if (window.location.pathname.startsWith('/salon/login') || window.location.pathname === '/salon') {
          window.location.replace(SALON_HUB_PATH);
        }
      }, 0);
    } catch (e) {
      setError(e?.message || 'Unable to login right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="salon-login-page">
      <div className="salon-login-ambient" aria-hidden="true">
        <div className="salon-login-grid" />
        <span className="salon-login-orb salon-login-orb--1" />
        <span className="salon-login-orb salon-login-orb--2" />
      </div>

      <div className="salon-login-shell" role="region" aria-label="Salon login form">
        <section className="salon-login-card">
          <div className="salon-login-brand">
            <img src="/logo.svg" alt="Nyafaz" className="salon-login-logo" />
            <span className="salon-login-badge">Salon workspace</span>
          </div>

          <div className="salon-login-headline">
           
            <p>Sign in once to unlock skin analysis and the virtual beauty studio.</p>
          </div>

          <ul className="salon-login-features" aria-label="Included experiences">
            <li>Skin diagnostics</li>
            <li>Makeup &amp; hair studio</li>
          </ul>

          <form className="salon-login-form" onSubmit={handleSubmit}>
            <div className="salon-login-field">
              <User size={18} className="salon-login-field-icon" aria-hidden="true" />
              <input
                id="salon-username"
                className="salon-login-input"
                type="text"
                autoComplete="username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isSubmitting}
                aria-label="Username"
              />
            </div>

            <div className="salon-login-field">
              <Lock size={18} className="salon-login-field-icon" aria-hidden="true" />
              <input
                id="salon-password"
                className="salon-login-input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                aria-label="Password"
              />
              <button
                type="button"
                className="salon-login-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={isSubmitting}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <label className="salon-login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => onRememberMeToggle(REMEMBER_PREFIX, e.target.checked, setRememberMe)}
                disabled={isSubmitting}
              />
              Remember me
            </label>

            {error ? <p className="salon-login-error" role="alert">{error}</p> : null}

            <button className="salon-login-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Continue to workspace'}
            </button>
          </form>

          <p className="salon-login-footnote">
            Secure salon access for diagnostics, routines, and live beauty previews.
          </p>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
